import {
  Flex,
  Text,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { DeliverableContainerOutputLenderViewGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  calculateLenderContainerStatus,
  LenderStatus,
  LenderStatusCell,
} from '../Status';
import { getDeliverableFinalOutputReceiptsViewUrl } from '../utils';
import { Icon } from 'packages/common-frontend/components/icons';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { useGetDeliverablesContainersLenderViewQuery } from '~/core/routes/deliverables/queries/GetDeliverablesContainersLenderView.gql.generated';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';

const LenderTaskCenterButton = ({
  container,
  status,
}: {
  container: DeliverableContainerOutputLenderViewGQLType;
  status: LenderStatus;
}) => {
  const navigate = useNavigate();

  const onClick = useCallback(() => {
    switch (status) {
      case 'overdue': {
        if (container.latestReceiptId) {
          navigate(
            getDeliverableFinalOutputReceiptsViewUrl({
              containerId: container.id,
              receiptId: container.latestReceiptId,
            }),
          );
        }
        break;
      }
      case 'remediationRequested':
      case 'readyForReview':
        finleyAssert(
          container.latestReceiptId,
          `Cannot have status: ${status} without a latestReceiptId`,
        );
        navigate(
          getDeliverableFinalOutputReceiptsViewUrl({
            containerId: container.id,
            receiptId: container.latestReceiptId,
          }),
        );
        break;
      default:
        break;
    }
  }, [container.id, container.latestReceiptId, navigate, status]);
  return (
    <Button
      whiteSpace="normal"
      textAlign="left"
      px={4}
      py={8}
      fontWeight="normal"
      fontSize="sm"
      rightIcon={
        container.latestReceiptId ? (
          <Icon icon="chevron-right" color="blue.300" />
        ) : (
          <></>
        )
      }
      onClick={onClick}
      isDisabled={!container.latestReceiptId}
      _disabled={{ opacity: 1, cursor: 'inherit' }}
    >
      {`${commonUtils.toTitleCase(
        container.creditFacilitySlug,
      )} \n ${commonUtils.toTitleCase(
        container.configKey,
      )} - ${commonDate.fullDateStrToHumanDateString(container.dueDate)}`}
    </Button>
  );
};

const LenderTaskCenterByStatus = ({
  containersWithStatus,
}: {
  containersWithStatus: DeliverableContainerOutputLenderViewGQLType[];
}) => {
  if (containersWithStatus.length === 0) {
    return <></>;
  }

  const status = calculateLenderContainerStatus(containersWithStatus[0]);
  return (
    <>
      <LenderStatusCell status={status} fontWeight="semibold" />
      {containersWithStatus.map((container) => {
        return (
          <LenderTaskCenterButton
            key={`${container.id}-${status}`}
            container={container}
            status={status}
          />
        );
      })}
    </>
  );
};

type NextStepsContainers = {
  overdue: DeliverableContainerOutputLenderViewGQLType[];
  remediationRequested: DeliverableContainerOutputLenderViewGQLType[];
  readyForReview: DeliverableContainerOutputLenderViewGQLType[];
  size: number;
};

const getNextStepsContainersLender = (
  containers?: DeliverableContainerOutputLenderViewGQLType[],
): NextStepsContainers => {
  const overdue = [];
  const remediationRequested = [];
  const readyForReview = [];
  let size = 0;

  if (containers) {
    for (const container of containers) {
      const status = calculateLenderContainerStatus(container);

      switch (status) {
        case 'readyForReview':
          readyForReview.push(container);
          size += 1;
          break;
        case 'overdue':
          overdue.push(container);
          size += 1;
          break;
        case 'remediationRequested':
          remediationRequested.push(container);
          size += 1;
          break;
        default:
          break;
      }
    }
  }

  return {
    readyForReview,
    overdue,
    remediationRequested,
    size,
  };
};

const TaskCenterDrawerBase = () => {
  const taskDrawerControls = useDisclosure();

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );
  const deliverableContainersQuery = useGetDeliverablesContainersLenderViewQuery(
    { variables: { creditFacilityIds } },
  );
  if (deliverableContainersQuery.error) throw deliverableContainersQuery.error;

  const nextStepContainers = getNextStepsContainersLender(
    deliverableContainersQuery.data?.deliverablesContainersLenderView,
  );
  const hasPendingTasks = nextStepContainers.size > 0;
  return (
    <>
      <IconButtonV2
        isLoading={deliverableContainersQuery.loading}
        variant="primary-muted"
        placement="bottom"
        aria-label={hasPendingTasks ? 'Pending Tasks' : 'Tasks'}
        hasDotIndicator={hasPendingTasks}
        icon={<Icon icon="list-check" />}
        isActive={taskDrawerControls.isOpen}
        onClick={taskDrawerControls.onToggle}
      />
      <Drawer {...taskDrawerControls} placement="right" closeOnOverlayClick>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Tasks</DrawerHeader>
          <DrawerBody>
            {(() => {
              if (!hasPendingTasks) {
                return <Text>Currently, there are no active tasks.</Text>;
              }

              return (
                <Flex direction="column" gap="1.5rem">
                  <LenderTaskCenterByStatus
                    containersWithStatus={nextStepContainers.overdue}
                  />
                  <LenderTaskCenterByStatus
                    containersWithStatus={
                      nextStepContainers.remediationRequested
                    }
                  />
                  <LenderTaskCenterByStatus
                    containersWithStatus={nextStepContainers.readyForReview}
                  />
                </Flex>
              );
            })()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Exports
export const TaskCenterDrawer = () => (
  <ErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return (
        // Disabled icon button as fallback UI
        <IconButtonV2
          isDisabled
          variant="primary-muted"
          placement="bottom"
          aria-label="Tasks"
          icon={<Icon icon="list-check" />}
        />
      );
    }}
  >
    <TaskCenterDrawerBase />
  </ErrorBoundary>
);
